import { useState } from "react";
import { Link } from "wouter";
import { useListAllTickets } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDate } from "@/lib/format";
import { ChevronRight, TicketCheck } from "lucide-react";

const typeLabels: Record<string, string> = {
  bug: "Bug Report",
  feature_request: "Feature Request",
  other: "Other",
};

const priorityColors: Record<string, string> = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-blue-100 text-blue-700",
  high: "bg-orange-100 text-orange-700",
  urgent: "bg-red-100 text-red-700",
};

const priorityLabels: Record<string, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  urgent: "Urgent",
};

const statusColors: Record<string, string> = {
  open: "bg-green-100 text-green-700",
  in_progress: "bg-blue-100 text-blue-700",
  resolved: "bg-slate-100 text-slate-600",
  closed: "bg-slate-100 text-slate-500",
};

const statusLabels: Record<string, string> = {
  open: "Open",
  in_progress: "In Progress",
  resolved: "Resolved",
  closed: "Closed",
};

export default function AdminTickets() {
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [search, setSearch] = useState("");

  const { data: tickets, isLoading } = useListAllTickets({
    status: filterStatus || undefined,
    type: filterType || undefined,
    priority: filterPriority || undefined,
  });

  const filtered = tickets?.filter((t) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      t.title.toLowerCase().includes(q) ||
      t.submitterEmail.toLowerCase().includes(q) ||
      t.submitterName.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">All Tickets</h1>
        <p className="text-muted-foreground">View and manage all support tickets.</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <Input
          placeholder="Search title or submitter..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
        />
        <Select value={filterStatus || "_all"} onValueChange={(v) => setFilterStatus(v === "_all" ? "" : v)}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterType || "_all"} onValueChange={(v) => setFilterType(v === "_all" ? "" : v)}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Types</SelectItem>
            <SelectItem value="bug">Bug Report</SelectItem>
            <SelectItem value="feature_request">Feature Request</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterPriority || "_all"} onValueChange={(v) => setFilterPriority(v === "_all" ? "" : v)}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All Priorities</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-xl" />
          ))}
        </div>
      ) : !filtered || filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <TicketCheck className="h-12 w-12 text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">No tickets found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((ticket) => (
            <Link key={ticket.id} href={`/support/${ticket.id}`}>
              <Card className="cursor-pointer hover:border-primary/40 transition-colors">
                <CardContent className="py-4 px-4 sm:px-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex items-start gap-2 flex-wrap">
                        <span className="font-medium break-words min-w-0">{ticket.title}</span>
                        <Badge
                          variant="secondary"
                          className={`text-xs shrink-0 ${priorityColors[ticket.priority] ?? ""}`}
                        >
                          {priorityLabels[ticket.priority]}
                        </Badge>
                        <Badge
                          variant="secondary"
                          className={`text-xs shrink-0 ${statusColors[ticket.status] ?? ""}`}
                        >
                          {statusLabels[ticket.status]}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-1.5 gap-y-0.5">
                        <span>{typeLabels[ticket.type]}</span>
                        <span className="hidden sm:inline">·</span>
                        <span className="break-all min-w-0">
                          {ticket.submitterName} ({ticket.submitterEmail})
                        </span>
                        <span className="hidden sm:inline">·</span>
                        <span>{formatDate(ticket.createdAt)}</span>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
